import { useState, useEffect, useCallback, useRef } from "react";
import { medicinesService } from "@/services/medicine.service";
import { CreateMedicineDto, Medicine } from "@/types/medicine";
import { FilterDto, BackendSort } from "@/hooks/filterUtils";
import { FilterOperation, FilterType } from "@/types/filter";
import { filterKeyToConfigMap } from "@/config/medicine-table.config";

export const useMedicines = () => {
  // Async search state
  const [searchResults, setSearchResults] = useState<Medicine[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchPage, setSearchPage] = useState(1);
  const [searchHasMore, setSearchHasMore] = useState(false);
  const [currentSearchQuery, setCurrentSearchQuery] = useState("");
  const searchPageSize = 10; // Increased from 5 for better UX
  const isLoadingMoreRef = useRef(false); // Prevent race conditions

  // Async search handler for autocomplete - resets to page 1
  const searchMedicines = useCallback(
    async (query: string) => {
      setSearchLoading(true);
      setCurrentSearchQuery(query);
      setSearchPage(1);
      try {
        const filterDto = {
          filters: {
            medicine_name: {
              operation: FilterOperation.STARTSWITH,
              value: query,
              type: FilterType.STRING,
            },
          },
          sort: [],
          pagination: { page: 1, pageSize: searchPageSize },
        };
        const res = await medicinesService.getMedicines(filterDto);
        setSearchResults(res.data || []);
        setSearchHasMore((res.data?.length || 0) >= searchPageSize);
        return res.data || [];
      } catch (e) {
        setSearchResults([]);
        setSearchHasMore(false);
        return [];
      } finally {
        setSearchLoading(false);
      }
    },
    [searchPageSize]
  );

  // Load more results for infinite scroll
  const loadMoreSearchResults = useCallback(async () => {
    // Prevent duplicate requests with ref check (immediate, unlike state)
    if (
      isLoadingMoreRef.current ||
      searchLoading ||
      !searchHasMore ||
      !currentSearchQuery
    )
      return;

    isLoadingMoreRef.current = true;
    setSearchLoading(true);
    const nextPage = searchPage + 1;

    try {
      const filterDto = {
        filters: {
          medicine_name: {
            operation: FilterOperation.STARTSWITH,
            value: currentSearchQuery,
            type: FilterType.STRING,
          },
        },
        sort: [],
        pagination: { page: nextPage, pageSize: searchPageSize },
      };
      const res = await medicinesService.getMedicines(filterDto);
      const newResults = res.data || [];

      // Deduplicate by _id to prevent duplicate entries
      setSearchResults((prev) => {
        const existingIds = new Set(prev.map((m) => m._id));
        const uniqueNewResults = newResults.filter(
          (m) => !existingIds.has(m._id)
        );
        return [...prev, ...uniqueNewResults];
      });
      setSearchPage(nextPage);
      setSearchHasMore(newResults.length >= searchPageSize);
    } catch (e) {
      console.error("Error loading more medicines:", e);
    } finally {
      isLoadingMoreRef.current = false;
      setSearchLoading(false);
    }
  }, [
    searchLoading,
    searchHasMore,
    currentSearchQuery,
    searchPage,
    searchPageSize,
  ]);
  const [data, setData] = useState<Medicine[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sort, setSort] = useState<{
    key: keyof Medicine;
    direction: "asc" | "desc";
  } | null>(null);
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const backendSort: BackendSort[] = [];
      if (sort) {
        backendSort.push({
          key: sort.key,
          direction: sort.direction,
        });
      }

      const filterDto: FilterDto = {
        filters: {},
        sort: backendSort,
        pagination: { page, pageSize },
      };
      if (Object.keys(filters).length > 0) {
        filterDto.filters = Object.entries(filters).reduce(
          (acc, [key, value]) => {
            if (value !== undefined && value !== null && value !== "") {
              let operation = FilterOperation.CONTAINS;
              let type = FilterType.STRING;
              const filterType = filterKeyToConfigMap[key];

              if (filterType === "select") {
                operation = FilterOperation.EQUAL;
                type = FilterType.NUMBER;
              }
              acc[key] = { operation, value, type };
            }
            return acc;
          },
          {}
        );
      }

      const response = await medicinesService.getMedicines(filterDto);
      setData(response.data);
      setTotal(response.total);
    } catch (err) {
      console.error("Error fetching medicines:", err);
      setError("Failed to load medicines. Please try again.");
      setData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, sort, filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getMedicine = async (id: string) => {
    try {
      return await medicinesService.getMedicine(id);
    } catch (err) {
      console.error("Error fetching medicine:", err);
      return null;
    }
  };

  const refreshData = () => {
    fetchData();
    setPage((current) => current);
  };

  const createMedicine = async (data: CreateMedicineDto) => {
    try {
      const newMed = await medicinesService.createMedicine(data);
      refreshData();
      return newMed;
    } catch (err) {
      console.error("Error creating medicine:", err);
      throw err;
    }
  };

  const updateMedicine = async (id: string, data: Partial<Medicine>) => {
    try {
      const result = await medicinesService.updateMedicine(id, data);
      refreshData();
      return result;
    } catch (err) {
      console.error("Error updating medicine:", err);
      throw err;
    }
  };

  const deleteMedicine = async (id: string) => {
    try {
      const result = await medicinesService.deleteMedicine(id);
      refreshData();
      return result;
    } catch (err) {
      console.error("Error deleting medicine:", err);
      throw err;
    }
  };

  return {
    data,
    total,
    page,
    setPage,
    pageSize,
    setPageSize,
    sort,
    setSort,
    filters,
    setFilters,
    loading,
    error,
    getMedicine,
    createMedicine,
    updateMedicine,
    deleteMedicine,
    // For async autocomplete with infinite scroll
    searchMedicines,
    searchResults,
    searchLoading,
    searchHasMore,
    loadMoreSearchResults,
  };
};
